import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AuthLayout } from "@/components/auth/auth-layout";
import { RedirectToLogin } from "@/components/auth/redirect";
import type { TripOutcome } from "@/components/github/logic";
import {
  createSlackCompleter,
  parseSlackCallbackSearch,
  slackDestination,
  type SlackCompletion,
} from "@/components/slack/logic";
import { api } from "@/lib/api";
import { convex } from "@/lib/convex";
import { useAuthStatus } from "@/lib/use-auth-status";

export const Route = createFileRoute("/slack/callback")({
  component: SlackCallbackPage,
});

// One for the life of the page, outside React, as on /github/callback.
const finish = createSlackCompleter(
  (params) => convex.action(api.slack.connect.complete, params),
  {
    getItem: (key) => window.sessionStorage.getItem(key),
    setItem: (key, value) => window.sessionStorage.setItem(key, value),
  },
);

/**
 * Where the browser lands after adding Echoes to a Slack workspace, handed on
 * by the Convex site's `/slack/oauth`. Only the person who started the trip
 * can finish it, so this needs a session: a signed-out arrival goes to log
 * in and comes back here.
 */
function SlackCallbackPage() {
  const status = useAuthStatus();
  if (status === "signedOut") return <RedirectToLogin />;
  return (
    <>
      <Working />
      {status === "signedIn" ? <Finish /> : null}
    </>
  );
}

function Working() {
  const { t } = useTranslation("slack");
  return (
    <AuthLayout title={t("callback.title")}>
      <p role="status" className="flex items-center gap-2 text-muted-foreground">
        <Loader
          aria-hidden
          strokeWidth={1.75}
          className="size-3.5 shrink-0 animate-[spin_1.2s_linear_infinite]"
        />
        {t("callback.working")}
      </p>
    </AuthLayout>
  );
}

/** Hands what Slack gave back to the server, once, and leaves with the answer. */
function Finish() {
  const { t } = useTranslation("slack");
  const navigate = useNavigate();
  const [params] = useState(() => parseSlackCallbackSearch(window.location.search));
  const leave = useRef({ navigate, t });
  useEffect(() => {
    leave.current = { navigate, t };
  });

  useEffect(() => {
    let left = false;
    const outcome: Promise<TripOutcome<SlackCompletion>> = params
      ? finish(params)
      : Promise.resolve({
          kind: "finished",
          completion: { workspaceSlug: null, result: "error", reason: "state_invalid" },
        });
    void outcome.then((result) => {
      if (left) return;
      const { navigate, t } = leave.current;
      const destination = slackDestination(result);
      if (destination.to === "integrations") {
        void navigate({
          to: "/$workspaceSlug/settings/integrations",
          params: { workspaceSlug: destination.workspaceSlug },
          search: destination.search,
          replace: true,
        });
        return;
      }
      if (destination.reason) {
        toast.error(t("callback.failed"), {
          description: t(`callback.reasons.${destination.reason}`),
          duration: 12_000,
        });
      }
      void navigate({ to: "/", replace: true });
    });
    return () => {
      left = true;
    };
  }, [params]);

  return null;
}
